import React, { useState } from "react";
import { toast } from "sonner";
import { updateRunbookIntake } from "../lib/api";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ClipboardCheck, ChevronDown } from "lucide-react";

// Discovery questionnaire captured at the very start of a site's migration.
// Answers feed the plan, the AI guide and the estate audit.
export const RunbookIntake = ({ siteId, schema = [], answers, setAnswers, track = "webex" }) => {
  const [open, setOpen] = useState(true);

  const answered = Object.values(answers || {}).filter((v) => v !== undefined && v !== "").length;
  const total = schema.reduce((a, s) => a + s.fields.length, 0);

  const save = async (id, value) => {
    const next = { ...(answers || {}), [id]: value };
    setAnswers(next);
    try { await updateRunbookIntake(siteId, next, track); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="rounded-lg border border-blue-600/25 bg-zinc-900 overflow-hidden mb-6" data-testid="runbook-intake">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="intake-toggle"
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        <span className="h-8 w-8 rounded-md bg-blue-600/15 border border-blue-600/30 flex items-center justify-center shrink-0">
          <ClipboardCheck className="h-4 w-4 text-blue-400" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white">Discovery Questionnaire</div>
          <div className="text-[11px] text-zinc-500">Capture the existing environment first — it drives the plan, prefill and AI guidance.</div>
        </div>
        <span className="text-[12px] font-mono text-zinc-400 shrink-0">{answered}/{total}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="p-4 border-t border-zinc-800 space-y-5">
          {schema.map((sec) => (
            <div key={sec.section}>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">{sec.section}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {sec.fields.map((f) => (
                  <div key={f.id} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{f.label}</label>
                    {f.type === "select" ? (
                      <Select value={(answers || {})[f.id] || ""} onValueChange={(v) => save(f.id, v)}>
                        <SelectTrigger data-testid={`intake-${f.id}`} className="h-9 bg-zinc-950 border-zinc-800 text-[12px] mt-0.5">
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : f.type === "textarea" ? (
                      <textarea
                        data-testid={`intake-${f.id}`}
                        defaultValue={(answers || {})[f.id] || ""}
                        onBlur={(e) => { if (e.target.value !== ((answers || {})[f.id] || "")) save(f.id, e.target.value); }}
                        rows={2}
                        className="w-full resize-none bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-[12px] text-white mt-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <Input
                        data-testid={`intake-${f.id}`}
                        type={f.type === "number" ? "number" : "text"}
                        defaultValue={(answers || {})[f.id] || ""}
                        onBlur={(e) => { if (e.target.value !== ((answers || {})[f.id] || "")) save(f.id, e.target.value); }}
                        className="h-9 bg-zinc-950 border-zinc-800 text-[12px] mt-0.5"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
